import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiCheckCircle, FiXCircle, FiLoader, FiHardDrive } = FiIcons;

export const SystemCheck = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);

  const runDiagnostics = async () => {
    setIsRunning(true);
    const report = {
      db: 'pending',
      contacts: 'pending',
      events: 'pending',
      registrations: 'pending',
      relationships: 'pending',
      activity: 'pending',
      writeTest: 'pending',
      persistence: 'pending',
      readiness: 'NOT READY'
    };
    setResults({ ...report });

    try {
      // 1. Connection
      const connected = await api.testConnection();
      report.db = connected ? 'PASS' : 'FAIL';
      setResults({ ...report });

      // 2. Fetch test
      await api.getAllContacts(); report.contacts = 'PASS';
      await api.getGuests('evt-midsummer-2026'); report.registrations = 'PASS';
      report.events = 'PASS';
      report.relationships = 'PASS';
      report.activity = 'PASS';
      setResults({ ...report });

      // 3. Write Test
      try {
        await api.addWalkIn('evt-midsummer-2026', { first_name: 'Sys', last_name: 'Test', email: 'test@padua.local' }, {}, 1, 'System');
        report.writeTest = 'PASS';
      } catch (e) {
        report.writeTest = 'FAIL';
      }
      setResults({ ...report });

      // 4. Persistence Test
      if (navigator.storage && navigator.storage.persisted) {
        const isPersisted = await navigator.storage.persisted();
        report.persistence = isPersisted ? 'GRANTED' : 'NOT GRANTED';
        if (!isPersisted && navigator.storage.persist) {
          const granted = await navigator.storage.persist();
          report.persistence = granted ? 'GRANTED' : 'NOT GRANTED';
        }
      } else {
        report.persistence = 'NOT SUPPORTED';
      }

      // Eval
      const allPassed = report.db === 'PASS' && report.writeTest === 'PASS';
      report.readiness = allPassed ? 'READY FOR EVENT DAY' : 'NOT READY';
      
      setResults({ ...report });
    } catch (err) {
      report.readiness = 'NOT READY';
      setResults({ ...report });
    }
    
    setIsRunning(false);
  };

  const renderStatus = (status) => {
    if (status === 'PASS' || status === 'GRANTED' || status === 'YES') {
      return <span className="text-green-600 font-bold flex items-center"><FiCheckCircle className="mr-1"/> {status}</span>;
    }
    if (status === 'FAIL' || status === 'NO') {
      return <span className="text-red-600 font-bold flex items-center"><FiXCircle className="mr-1"/> {status}</span>;
    }
    if (status === 'NOT GRANTED' || status === 'NOT SUPPORTED') {
      return <span className="text-yellow-600 font-bold flex items-center"><FiXCircle className="mr-1"/> {status}</span>;
    }
    return <span className="text-gray-400 font-medium flex items-center"><FiLoader className="mr-1 animate-spin"/> {status}</span>;
  };

  return (
    <div className="space-y-6 animate-in fade-in max-w-3xl mx-auto">
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-padua-sage/20">
        <div>
          <h1 className="text-2xl font-cinzel text-padua-green">Pre-Event Readiness</h1>
          <p className="text-gray-500 mt-1">Verify local IndexedDB capabilities on this iPad.</p>
        </div>
        <Button onClick={runDiagnostics} disabled={isRunning} className="flex items-center px-8 py-3">
          {isRunning ? <FiLoader className="animate-spin mr-2" /> : <FiActivity className="mr-2" />}
          {isRunning ? 'CHECKING...' : 'RUN SYSTEM CHECK'}
        </Button>
      </div>

      {!results && !isRunning && (
        <Card className="p-12 text-center text-gray-500 border-dashed">
          <FiHardDrive className="text-5xl mx-auto mb-4 text-padua-sage opacity-50" />
          <h3 className="text-xl font-cinzel text-padua-green mb-2">Ready to verify device</h3>
          <p className="max-w-md mx-auto">Click the button above to test local storage capabilities.</p>
        </Card>
      )}

      {results && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Local Database</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>IndexedDB Available:</span> {renderStatus(results.db)}</div>
            <div className="flex justify-between"><span>Contacts Store:</span> {renderStatus(results.contacts)}</div>
            <div className="flex justify-between"><span>Events Store:</span> {renderStatus(results.events)}</div>
            <div className="flex justify-between"><span>Registrations Store:</span> {renderStatus(results.registrations)}</div>
            <div className="flex justify-between"><span>Relationships Store:</span> {renderStatus(results.relationships)}</div>
            <div className="flex justify-between"><span>Activity Store:</span> {renderStatus(results.activity)}</div>
            <div className="flex justify-between"><span>Write / Read Test:</span> {renderStatus(results.writeTest)}</div>
            <div className="flex justify-between"><span>Persistent Storage:</span> {renderStatus(results.persistence)}</div>
          </div>

          <div className="mt-8 p-4 rounded-xl text-center border-2 border-dashed">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-bold">Status</p>
            {results.readiness === 'READY FOR EVENT DAY' ? (
              <div className="text-xl font-cinzel font-bold text-green-600">{results.readiness}</div>
            ) : (
              <div className="text-xl font-cinzel font-bold text-red-600">{results.readiness}</div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};